import React from "react";
import styled from "styled-components";
import moment from "moment";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelHeader as PanelHeaderLayout } from "@sonder/ui/source/styled/layouts/PanelHeader";
import { colors } from "@sonder/ui/source/styled/theme";
import { TaskData, TaskSubjectType } from "core/queries/GetTask";
import {
  getTaskAssigneeIds,
  getTaskCompanyIds,
  getTaskDescription,
  getTaskDueBy,
  getTaskFutureCheckOuts,
  getTaskInternalRef,
  getTaskPriority,
} from "core/queries/mappers";
import {
  AssigneePicker,
  AssigneePickerTrigger,
} from "tasks/components/AssigneePicker";
import { TaskDescription } from "tasks/components/TaskDescription";
import {
  TaskDueByDate,
  TaskDueByDateTrigger,
} from "tasks/components/TaskDueByDate";
import { TaskPanelTabs } from "tasks/components/TaskPanel/TaskPanelTabs";
import { TaskPriority } from "tasks/components/TaskPriority";
import { isCleanTask, isSpaceCleanTask } from "tasks/helpers";

// Helpers
const convertToMoment = (date: string | null) => (date ? moment(date) : null);

// Fragments
const PanelHeader = styled(PanelHeaderLayout)`
  position: relative;
`;
const Priority = styled(TaskPriority)`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
`;
const Location = styled.span`
  color: ${colors.gray4};
  margin-bottom: 1rem;
`;
const TaskDescriptionHeader = styled(TaskDescription)`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  padding-right: 3rem;
`;
const TaskHeaderActions = styled(InputGroup)`
  margin-left: -0.75rem;
`;

interface HeaderLoadingProps {
  message?: JSX.Element | string;
}

export const HeaderLoading = ({ message }: HeaderLoadingProps) => (
  <PanelHeader>{message}</PanelHeader>
);

interface HeaderProps {
  task: TaskData;
}
export const Header = ({ task }: HeaderProps) => (
  <PanelHeader>
    <Priority
      eventName="task_panel"
      priority={getTaskPriority(task)}
      taskId={task.id}
    />
    <PanelCard>
      <TaskDescriptionHeader
        description={getTaskDescription(task)}
        eventName="task_panel"
        taskId={task.id}
      />
      <Location>{getTaskInternalRef(task)}</Location>
      <TaskHeaderActions>
        <AssigneePicker
          assigneeIds={getTaskAssigneeIds(task)}
          companyIds={getTaskCompanyIds(task)}
          disabled={isSpaceCleanTask(task.type)}
          eventName="task_panel"
          renderTrigger={({ assignees, onClear }) => (
            <AssigneePickerTrigger assignees={assignees} onClear={onClear} />
          )}
          tags={task.tags}
          taskCityId={task.city?.id}
          taskId={task.id}
          taskType={task.type}
        />
        <TaskDueByDate
          alignContent="right"
          bookingInfo={task.unit?.bookingInfo}
          date={convertToMoment(getTaskDueBy(task))}
          disabled={isCleanTask(task.type)}
          eventName="task_panel"
          futureCheckouts={getTaskFutureCheckOuts(task)}
          recurrenceSetting={task.tomTaskRecurrenceSetting}
          renderTrigger={props => <TaskDueByDateTrigger {...props} />}
          subjectType={task.subjectType as TaskSubjectType}
          taskId={task.id}
        />
      </TaskHeaderActions>
    </PanelCard>
    <TaskPanelTabs task={task} />
  </PanelHeader>
);
